import {
  useFonts,
  useImages,
  useLoader,
  useNavigateQuery,
  useNetworkListener
} from '@CS/hooks';

import {
  Logo,
  Button,
  MenuHR,
  MenuEN,
  AboutHR,
  AboutEN,
  Hotspots,
  FallbackHR,
  FallbackEN
} from '@/assets/images';

import Time from '@/utils/Time';
import API from '@/services/api';
import { connect } from 'react-redux';
import { useRecaptcha } from '@/hooks';
import FONTS from '@/assets/fonts.json';
import { isDEV } from '@/utils/Location';
import { User, MCD } from '@/services/app';
// import { sentryDSN } from '@/utils/API';
import { useState, useEffect } from 'react';
import { Login, Landing, Game } from '@/routes';

import { getLogged } from '@/redux/auth/selectors';
import type { UserState } from '@/redux/user/types';
import { RequestError } from '@CS/components/Popups';
import { getUserState } from '@/redux/user/selectors';
import type { RequestErrorInfo } from '@/services/api/types';
import { Routes, Route, useLocation } from 'react-router-dom';
import { AppWrapper, ContentWrapper } from '@CS/app/App.styles';
// import { withSentryReactRouterV6Routing } from '@sentry/react';
import TranslationProvider from '@CS/components/TranslationProvider';

type ApplicationProps = {
  user: UserState;
  logged?: boolean;
};

const Application = ({ user, logged }: ApplicationProps) => {
  const { search } = useLocation();

  const fontsLoaded = useFonts(FONTS);
  const navigate = useNavigateQuery();

  const network = useNetworkListener();
  const { recaptcha } = useRecaptcha();

  const [loading, setLoading] = useState(true);
  const { email, points, country } = user ?? {};

  const cancelLogin = () => setLoginError(void 0);
  const [landscape, setLandscape] = useState(false);

  const [loginError, setLoginError] = useState<RequestErrorInfo>();

  const imagesLoaded = useImages({
    ...Hotspots,
    FallbackEN,
    FallbackHR,
    AboutEN,
    AboutHR,
    MenuEN,
    MenuHR,
    Button,
    Logo
  });

  const loader = useLoader(
    loading || !fontsLoaded || !imagesLoaded || !recaptcha,
    { image: true }
  );

  const onVisibilityChange = () =>
    document.visibilityState === 'visible' && toggleFullscreen();

  const getUserData = () =>
    Promise.all([User.checkUserPoints(), User.checkUserData()]);

  const toggleLandscape = (landscape = false) => {
    MCD.orientationScreen(landscape);
    setLandscape(landscape);
  };

  const toggleFullscreen = () => {
    if (isDEV()) return;
    MCD.toggleFullscreen();
    MCD.orientationScreen();
  };

  const initApplication = () => {
    User.checkUserLanguage();
    User.checkUserCountry();
    toggleFullscreen();
    API.revokeToken();
    handleAuth();
  };

  const loginRedirect = () => {
    setLoading(false);
    navigate('/login');
  };

  const onLogin = async () => {
    toggleFullscreen();
    await getUserData();
    setLoading(false);
    navigate('/');
  };

  const homeRedirect = () => {
    setLoading(false);
    !isDEV() && navigate('/');
  };

  const handleAuth = () =>
    (!email || typeof points !== 'number') &&
    getUserData().then(homeRedirect).catch(loginRedirect);

  useEffect(() => {
    const userInfo = country && email;
    const ready = recaptcha && !logged;

    ready &&
      userInfo &&
      API.login(user).catch((error) =>
        setLoginError(API.getRequestErrorInfo(error))
      );
  }, [recaptcha, logged, country, email, user]);

  useEffect(() => {
    MCD.App.mcd && !loading && initApplication();
  }, [MCD.App.mcd]);

  useEffect(() => {
    Time.setCurrentTime(search);
  }, [search]);

  useEffect(() => {
    document.addEventListener('visibilitychange', onVisibilityChange);
    document.addEventListener('mcdBridgeReady', initApplication);
    (MCD.App.mcd || isDEV()) && initApplication();

    return () => {
      document.removeEventListener('mcdBridgeReady', initApplication);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  /**
   * Sentry Tracing Routes Wrapper:
   *
  const RoutesWrapper = sentryDSN
    ? withSentryReactRouterV6Routing(Routes)
    : Routes; */

  return (
    loader ||
    network || (
      <AppWrapper $landscape={landscape}>
        <TranslationProvider lang={user.language}>
          <ContentWrapper lang={user.language}>
            <Routes>
              {/* <RoutesWrapper> */}
              <Route path="/login" element={<Login onLogin={onLogin} />} />
              <Route path="/" element={<Landing onLogin={onLogin} />} />
              <Route
                path="/game/:index"
                element={<Game toggleLandscape={toggleLandscape} />}
              />
              {/* </RoutesWrapper> */}
            </Routes>

            {loginError && (
              <RequestError info={loginError} onClose={cancelLogin} />
            )}
          </ContentWrapper>
        </TranslationProvider>
      </AppWrapper>
    )
  );
};

const mapStateToProps = (state: never) => ({
  user: getUserState(state),
  logged: getLogged(state)
});

export default connect(mapStateToProps)(Application);
